import type { SceneDoc } from '@spirits/shared';

// Center-panel view for a scene "page" node: the scene's name, its "story so
// far" history, and the full setting description. Shown when a scene node in the
// feed is selected (mirrors how a bot node shows the chat).
export function ScenePage({
  scene,
  isCurrent,
  nextLabel,
  onNext,
  onClose,
}: {
  scene: SceneDoc & { id: string };
  isCurrent: boolean;
  nextLabel?: string | null;
  onNext?: () => void;
  onClose: () => void;
}) {
  return (
    <div className="h-full flex flex-col border-l border-slate-900 bg-slate-950 min-h-0">
      <div className="flex-none border-b border-slate-800 p-4 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <button
            type="button"
            onClick={onClose}
            className="lg:hidden text-slate-400 hover:text-slate-200 -ml-1 px-1 text-xl leading-none"
            title="back"
          >
            ←
          </button>
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-[0.2em] text-amber-300/70">
              Scene {scene.order + 1}
              <span className={`ml-2 ${isCurrent ? 'text-emerald-300/80' : 'text-slate-500'}`}>
                {isCurrent ? '· now' : '· past'}
              </span>
            </p>
            <h2 className="text-2xl text-slate-100 truncate">{scene.name}</h2>
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="hidden lg:block text-slate-500 hover:text-slate-300 text-lg leading-none flex-none"
          title="close"
        >
          ✕
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-6 min-h-0">
        <div className="max-w-2xl mx-auto space-y-5">
          {scene.blurb && <p className="text-sm leading-snug text-amber-200/70">{scene.blurb}</p>}
          {scene.description && (
            <p className="text-[15px] leading-relaxed text-slate-300 whitespace-pre-line">{scene.description}</p>
          )}
        </div>
      </div>

      {onNext && (
        <div className="flex-none border-t border-slate-800 p-3 flex justify-center">
          <button
            type="button"
            onClick={onNext}
            className="rounded-lg border border-amber-400/60 bg-amber-500/80 hover:bg-amber-400 text-slate-950 font-medium px-4 py-1.5 text-sm cursor-pointer"
          >
            {nextLabel ? `Next: ${nextLabel} →` : 'Next →'}
          </button>
        </div>
      )}
    </div>
  );
}
